import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { ArrowLeft, SoccerBall, ArrowsLeftRight, Cards, ShieldCheck } from '@phosphor-icons/react';

// Safe stat getter that handles both old format (flat stats) and new format (nested by comp)
const getStat = (player, key, comp = 'total') => {
  if (!player?.stats) return 0;
  const stats = player.stats;
  // New format: stats.league.goals, stats.cup.goals, etc.
  if (typeof stats.league === 'object' && stats.league !== null) {
    if (comp === 'total') {
      let total = 0;
      for (const c of ['league', 'cup', 'championsLeague', 'cwc', 'worldCup']) {
        total += (stats[c]?.[key] || 0);
      }
      return total;
    }
    return stats[comp]?.[key] || 0;
  }
  // Old flat format: stats.goals, stats.assists, etc.
  if (comp !== 'total' && comp !== 'league') return 0;
  return stats[key] || 0;
};

const STAT_TABS = [
  { key: 'goals', label: 'Goals', color: 'text-[#00E5FF]', activeClass: 'data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black data-[state=active]:shadow-[0_0_20px_rgba(0,229,255,0.35)]' },
  { key: 'assists', label: 'Assists', color: 'text-[#FF006E]', activeClass: 'data-[state=active]:bg-[#FF006E] data-[state=active]:text-white data-[state=active]:shadow-[0_0_20px_rgba(255,0,110,0.35)]' },
  { key: 'yellowCards', label: 'Yellow', color: 'text-[#FFB400]', activeClass: 'data-[state=active]:bg-[#FFB400] data-[state=active]:text-black data-[state=active]:shadow-[0_0_20px_rgba(255,180,0,0.35)]' },
  { key: 'redCards', label: 'Red', color: 'text-[#FF4D6D]', activeClass: 'data-[state=active]:bg-[#FF4D6D] data-[state=active]:text-white data-[state=active]:shadow-[0_0_20px_rgba(255,77,109,0.35)]' },
  { key: 'cleanSheets', label: 'Clean Sheets', color: 'text-[#CCFF00]', activeClass: 'data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black data-[state=active]:shadow-[0_0_20px_rgba(204,255,0,0.35)]' }
];

const COMP_OPTIONS = [
  { value: 'total', label: 'All Competitions' },
  { value: 'league', label: 'League' },
  { value: 'cup', label: "Emperor's Cup" },
  { value: 'championsLeague', label: 'Asian Cup' },
  { value: 'cwc', label: 'Club World Cup' },
  { value: 'worldCup', label: 'World Cup' },
];

export const StatsPage = ({ teams }) => {
  const navigate = useNavigate();
  const [comp, setComp] = useState('total');

  const allPlayers = useMemo(() =>
    (teams || []).flatMap(team => team.players.map(p => ({ ...p, teamName: team.name, teamCity: team.city }))),
    [teams]
  );

  const getTopPlayers = (statKey, limit = 30) =>
    [...allPlayers]
      .map(p => ({ ...p, statVal: getStat(p, statKey, comp) }))
      .filter(p => p.statVal > 0)
      .sort((a, b) => b.statVal - a.statVal)
      .slice(0, limit);

  const StatTable = ({ statKey, color }) => {
    const data = getTopPlayers(statKey);
    return (
      <div className="rounded-xl overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #0A0D1F 0%, #070A1A 100%)',
          border: '1px solid #1E2240',
          boxShadow: '0 1px 0 rgba(255,255,255,0.03) inset, 0 20px 60px rgba(0,0,0,0.35)',
        }}>
        <ScrollArea className="h-[450px]">
          {data.length === 0 ? (
            <div className="p-10 text-center">
              <p className="text-[#7A86B5] text-sm tracking-wide">No stats recorded yet.</p>
              <p className="text-xs text-[#4A5278] mt-1.5 tracking-wider uppercase">Simulate some matches first!</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-[#2C2C35] hover:bg-transparent">
                  <TableHead className="text-[#6E6E73] font-mono text-xs w-8">#</TableHead>
                  <TableHead className="text-[#6E6E73] font-mono text-xs">Player</TableHead>
                  <TableHead className="text-[#6E6E73] font-mono text-xs">Team</TableHead>
                  <TableHead className="text-[#6E6E73] font-mono text-xs text-center w-10">Age</TableHead>
                  <TableHead className="text-[#6E6E73] font-mono text-xs text-center w-14">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((player, idx) => (
                  <TableRow key={player.id} className={`border-b border-[#2C2C35] ${idx % 2 === 0 ? 'bg-[#0A0A0C]' : 'bg-[#121216]'} hover:bg-[#1A1A1F]`}>
                    <TableCell className="font-mono text-sm text-[#A0A0A5]">{idx + 1}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{player.flag || ''}</span>
                        <div>
                          <p className="text-sm font-medium text-white">{player.name}</p>
                          <p className="text-[10px] text-[#6E6E73]">{player.position} | OVR {player.overall}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-[#A0A0A5] truncate max-w-[120px]">{player.teamName}</TableCell>
                    <TableCell className="font-mono text-sm text-center text-[#A0A0A5]">{player.age}</TableCell>
                    <TableCell className={`font-mono text-sm text-center font-bold ${color}`}>{player.statVal}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </ScrollArea>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#04060F] pt-20 pb-8 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button onClick={() => navigate('/')} variant="ghost" className="text-[#A0A0A5] hover:text-white" data-testid="back-home-btn">
            <ArrowLeft size={20} />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading font-black text-3xl sm:text-4xl text-white uppercase tracking-wide">Stats</h1>
            <p className="text-[#A0A0A5] mt-1">League-wide player statistics</p>
          </div>
          <Select value={comp} onValueChange={setComp}>
            <SelectTrigger className="w-[180px] bg-[#0A0A0C] border-[#2C2C35] text-white text-sm" data-testid="comp-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121216] border-[#2C2C35]">
              {COMP_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <Tabs defaultValue="goals" className="w-full">
          <TabsList className="bg-[#0A0D1F] border border-[#1E2240] w-full grid grid-cols-5 mb-6 p-1 rounded-md">
            {STAT_TABS.map(t => (
              <TabsTrigger key={t.key} value={t.key} className={`${t.activeClass} font-heading uppercase text-sm rounded-sm transition-all tracking-[0.08em]`} data-testid={`tab-${t.key}`}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {STAT_TABS.map(t => (
            <TabsContent key={t.key} value={t.key}>
              <StatTable statKey={t.key} color={t.color} />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
};
